import { useEffect, useState } from 'react';
import { TapPayInstance } from 'react-native-tappay';
import safeAwait from 'safe-await';

import { retryMethodByApplePay } from '../../../constants/payby';
import { translate } from '../../../i18n';
import { ApplePayPrime } from '../../../types/ApplePayPrimeResponse';
import { PaymentMethodFromApi } from '../../../types/Payment';
import { PaymentData } from '../../../types/TransactionDetail';
import deviceInfo from '../../../utils/deviceInfo';
import S from './style';
import utils from './utils';

type ApplePayInfo = Pick<
  PaymentMethodFromApi,
  'text' | 'maintaining_text' | 'is_allowed' | 'hint'
>;

interface PropsPaymentData extends PaymentData {
  PAY_NAME: string;
  created_at: number;
  transaction_hint: string;
  transaction_no: string;
  transaction_status: string;
}

interface Props {
  TapPay: TapPayInstance;
  webSetupApplePay: Function;
  isApplePayAvailable: () => Promise<boolean>;
  applePayInfo: ApplePayInfo | undefined;
  paymentData: PropsPaymentData;
  postApplePayPrime: (_args: {
    failure?: Function;
    prime: string;
    transactionID: number;
    success: Function;
  }) => void;
  showFullScreenLoading: Function;
  closeFullScreenLoading: Function;
  transactionID: number;
  updateApiCommonError: (_args: {
    contentText: string;
    positiveText: string;
    negativeText?: string;
  }) => void;
}

export default function RetryApplePayBtn({
  TapPay,
  applePayInfo,
  paymentData,
  postApplePayPrime,
  showFullScreenLoading,
  closeFullScreenLoading,
  transactionID,
  updateApiCommonError,
  webSetupApplePay,
  isApplePayAvailable,
}: Props) {
  const [btnState, setBtnState] = useState({
    isShown: false,
    couldRetry: false,
  });

  useEffect(() => {
    if (paymentData) {
      const isShown = retryMethodByApplePay(paymentData.PAY_NAME).length > 0;
      // Note:
      // props.TapPay from withTapPay may be undefined though loadSuccess

      isApplePayAvailable()
        .then(valid => {
          const couldRetry =
            valid && isShown && !deviceInfo.isInApp() && !!deviceInfo.isIOS();

          if (couldRetry) {
            webSetupApplePay({ totalPrice: paymentData.total_price });
          }

          setBtnState({
            isShown: couldRetry,
            couldRetry, // because the value is available on web only
          });
        })
        .catch(() => {
          // if browser dosen't support ApplePay, dont show ApplePay Button
          setBtnState({
            isShown: false,
            couldRetry: false,
          });
        });
    }
  }, [paymentData]);

  const onError = contentText => {
    updateApiCommonError({
      contentText,
      positiveText: translate('alert-default-positive-text'),
    });
  };
  const maintainingText =
    applePayInfo?.maintaining_text || translate('apple-pay-payment-failure');

  // Apple Web 跟其他不一樣因此抽出來寫
  const onPress = async () => {
    showFullScreenLoading();
    /**
     * 1. 由於 payment on web 有 UI 的限制(必須由使用者觸發，才能跳出 ApplePay)，
     * 因此要先跳出 ApplePay 取得 ApplePay Prime
     */
    const [applePayError, applePayResult] = await safeAwait(
      TapPay.webGetApplePayPrime()
    );

    const prime = applePayResult?.prime;
    const transactionApplePaySuccess = (data: ApplePayPrime) => {
      if (data.status) {
        utils.onPaid(transactionID, data);
      } else {
        onError(translate('api-common-error-msg-try-again'));
      }
      closeFullScreenLoading();
    };

    const transactionApplePayFailure = () => {
      onError(translate('api-common-error-msg-try-again'));
      closeFullScreenLoading();
    };

    // 若取得 ApplePayPrime Error，在這時候才能處理 error handling
    if (applePayError) {
      transactionApplePayFailure();
    }

    // 2. 最後用 ApplePay prime 交易此筆訂單
    if (prime) {
      postApplePayPrime({
        prime,
        transactionID,
        success: transactionApplePaySuccess,
        failure: transactionApplePayFailure,
      });
    } else {
      onError(maintainingText);
    }
  };

  if (!btnState.isShown) {
    return null;
  }

  return (
    <S.RetryApplePayBtn
      accessibilityRole="button"
      disabled={!btnState.couldRetry}
      onPress={onPress}
    >
      {btnState.couldRetry ? (
        <S.RetryApplePayText>
          {translate('orderDetail-retry-apple-pay')}
        </S.RetryApplePayText>
      ) : (
        <S.RetryApplePayLoading size="small" />
      )}
    </S.RetryApplePayBtn>
  );
}
